/**
 * useCampaignTracker Hook
 *
 * React hook for managing campaign tracking cookie with UTM parameters.
 * Handles consent gating and cookie lifecycle.
 *
 * Part of BEC-20520: Campaign Tracker with Salesforce
 *
 * **Important**: This hook registers OneTrust event listeners. To avoid duplicate
 * callbacks from repeated mounts, use this hook once at the app/layout level
 * (e.g., via a provider or context) rather than in many leaf components.
 * This is consistent with the pattern used in `useAlgoliaUserToken`.
 *
 * @example
 * ```tsx
 * const { hasCampaignData, campaignData } = useCampaignTracker({
 *   countryCode: 'US'
 * });
 * ```
 */

import { useCallback, useEffect, useState } from 'react';

import { useRouter } from '@/hooks/useRouter';
import type { CampaignData } from '@/lib/campaignTracker';
import {
  canSetCampaignCookie,
  getCampaignCookie,
  hasCampaignDataChanged,
  parseUtmParams,
  removeCampaignCookie,
  setCampaignCookie,
} from '@/lib/campaignTracker';

interface UseCampaignTrackerOptions {
  /** ISO 3166-1 alpha-2 country code for consent checking */
  countryCode: string;
}

interface UseCampaignTrackerReturn {
  /** Current campaign data from cookie (null if no cookie) */
  campaignData: CampaignData | null;
  /** Whether campaign data exists in cookie */
  hasCampaignData: boolean;
  /** Manually refresh campaign data from cookie */
  refreshCampaignData: () => void;
}

/**
 * Hook to manage campaign tracking cookie.
 *
 * Behavior:
 * - On mount/route change: parses UTM params from URL, sets cookie if consent allows
 * - If UTM params match existing cookie: extends cookie lifetime
 * - If UTM params differ: overwrites cookie with new values
 * - On consent revocation: deletes cookie immediately
 */
export const useCampaignTracker = ({
  countryCode,
}: UseCampaignTrackerOptions): UseCampaignTrackerReturn => {
  const router = useRouter();

  // Initialize state from existing cookie (client-side only)
  const [campaignData, setCampaignData] = useState<CampaignData | null>(() => {
    if (typeof window === 'undefined') return null;
    return getCampaignCookie();
  });

  /**
   * Process UTM params from current URL.
   * Sets/updates cookie if consent allows and UTM params are present.
   */
  const processUtmParams = useCallback(() => {
    const urlParams = parseUtmParams();
    if (!urlParams) return;

    // Check if we can set the cookie (consent check)
    if (!canSetCampaignCookie(countryCode)) return;

    const currentData = getCampaignCookie();

    // Set or update cookie if data changed
    if (hasCampaignDataChanged(currentData, urlParams)) {
      // Different params: overwrite cookie with new values
      setCampaignCookie(urlParams);
      setCampaignData(urlParams);
    } else if (currentData) {
      // Same params: extend cookie lifetime by re-setting
      setCampaignCookie(currentData);
    }
  }, [countryCode]);

  /**
   * Enforce consent gating for existing cookies.
   *
   * Dead-angle protection:
   * If we are in a consent-required region and consent is not granted (or was revoked),
   * ensure we do not keep or use an existing campaign cookie while waiting for OneTrust
   * events to fire.
   */
  useEffect(() => {
    if (!canSetCampaignCookie(countryCode)) {
      removeCampaignCookie();
      setCampaignData(null);
    }
  }, [countryCode]);

  // Process UTM params on mount and route changes
  useEffect(() => {
    processUtmParams();
  }, [router.asPath, processUtmParams]);

  // Listen for OneTrust consent changes
  useEffect(() => {
    /**
     * Handle consent changes from OneTrust.
     *
     * - If consent is granted: try to set cookie from current URL
     * - If consent is revoked: delete cookie immediately (per decision A3)
     */
    const handleConsentChange = () => {
      if (!canSetCampaignCookie(countryCode)) {
        // Consent revoked or not given: delete cookie and clear state
        removeCampaignCookie();
        setCampaignData(null);
        return;
      }

      // Consent granted: try to process current URL params
      processUtmParams();

      // Also refresh state in case cookie was set elsewhere
      setCampaignData(getCampaignCookie());
    };

    if (typeof window !== 'undefined') {
      // Listen for custom OneTrust event
      window.addEventListener('OneTrustGroupsUpdated', handleConsentChange);

      // Also register with OneTrust callback API if available
      const win = window as typeof window & {
        OneTrust?: { OnConsentChanged?: (callback: () => void) => void };
      };
      win.OneTrust?.OnConsentChanged?.(handleConsentChange);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(
          'OneTrustGroupsUpdated',
          handleConsentChange,
        );
      }
    };
  }, [countryCode, processUtmParams]);

  /**
   * Manually refresh campaign data from cookie.
   * Useful if cookie was modified externally.
   */
  const refreshCampaignData = useCallback(() => {
    setCampaignData(getCampaignCookie());
  }, []);

  return {
    campaignData,
    hasCampaignData: campaignData !== null,
    refreshCampaignData,
  };
};
